import { useState, useEffect, useMemo, useRef } from 'react';
import type { Point, Polygone, CategorieMarqueur, Projet } from '../../types/map';
import { validatePoint, validatePolygone, validateProjet } from './validators';
import { API_URL as apiUrl } from '../../config/api';

export interface UseMapDataReturn {
  points: Point[];
  polygones: Polygone[];
  categories: CategorieMarqueur[];
  /** Projets (regroupements d'emplacements), unité de résultat de recherche. */
  projets: Projet[];
  loading: boolean;
  error: string | null;
  mapKey: string;
  handleReload: () => void;
}

/** Associe à chaque projet ses emplacements (points déjà validés/fusionnés) par projetId. */
function attachPoints(rawProjets: unknown[], points: Point[]): Projet[] {
  return rawProjets
    .map(validateProjet)
    .filter((p): p is Omit<Projet, 'points'> => p !== null)
    .map((p) => ({ ...p, points: points.filter((pt) => pt.projetId === p.id) }));
}

export const useMapData = (slug?: string): UseMapDataReturn => {
  const [points, setPoints] = useState<Point[]>([]);
  const [polygones, setPolygones] = useState<Polygone[]>([]);
  const [categories, setCategories] = useState<CategorieMarqueur[]>([]);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const mapKey = useRef<string>(Math.random().toString(36).substring(7));

  useEffect(() => {
    const fetchMapData = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        if (slug) {
          // Mode slug: charger les données d'une carte spécifique
          const resp = await fetch(`${apiUrl}/cartes/${slug}/data`).catch(() => null);
          if (!resp) throw new Error('API injoignable');
          if (resp.status === 403) {
            setError('Carte non disponible (brouillon)');
            setPoints([]);
            setPolygones([]);
            setProjets([]);
            return;
          }
          if (!resp.ok) throw new Error(`Erreur API ${resp.status}`);
          
          const data = await resp.json();

          // Extraire les catégories de marqueur depuis les métadonnées de la carte
          const rawCategories = data?.metadata?.categories || data?.categories || [];
          if (Array.isArray(rawCategories)) {
            setCategories(rawCategories as CategorieMarqueur[]);
          }

          const rawPoints = Array.isArray(data.features) 
            ? data.features.filter((f: Record<string, unknown>) => 
                (f.properties as Record<string, unknown>)?.type === 'point'
              ) 
            : [];
          const rawPolys = Array.isArray(data.features) 
            ? data.features.filter((f: Record<string, unknown>) => 
                (f.properties as Record<string, unknown>)?.type === 'polygone'
              ) 
            : [];
          
          const validatedPoints = rawPoints
            .map(validatePoint)
            .filter((p: Point | null): p is Point => p !== null);
          const validatedPolygones = rawPolys
            .map(validatePolygone)
            .filter((g: Polygone | null): g is Polygone => g !== null);

          const rawProjets = Array.isArray(data?.metadata?.projets) ? data.metadata.projets : [];
          
          setPoints(validatedPoints);
          setPolygones(validatedPolygones);
          setProjets(attachPoints(rawProjets, validatedPoints));
          return;
        }

        // Mode legacy: points/polygones/projets globaux
        const [pointsResponse, polygonesResponse, projetsResponse] = await Promise.all([
          fetch(`${apiUrl}/points`).catch(() => null),
          fetch(`${apiUrl}/polygones`).catch(() => null),
          fetch(`${apiUrl}/projets?limit=1000`).catch(() => null)
        ]);

        // Traitement des points
        let validatedPoints: Point[] = [];
        if (pointsResponse && pointsResponse.ok) {
          const pointsData = await pointsResponse.json();
          
          const rawPoints: unknown[] = Array.isArray(pointsData) ? pointsData : 
            Array.isArray(pointsData.features) ? pointsData.features :
            Array.isArray(pointsData.points) ? pointsData.points :
            Array.isArray(pointsData.data) ? pointsData.data : [];

          validatedPoints = rawPoints
            .map(validatePoint)
            .filter((point): point is Point => point !== null);

          setPoints(validatedPoints);
        } else {
          setPoints([]);
        }

        // Traitement des polygones
        if (polygonesResponse && polygonesResponse.ok) {
          const polygonesData = await polygonesResponse.json();
          
          const rawPolygones: unknown[] = Array.isArray(polygonesData) ? polygonesData : 
            Array.isArray(polygonesData.features) ? polygonesData.features :
            Array.isArray(polygonesData.polygones) ? polygonesData.polygones :
            Array.isArray(polygonesData.data) ? polygonesData.data : [];

          const validatedPolygones = rawPolygones
            .map(validatePolygone)
            .filter((polygone): polygone is Polygone => polygone !== null);

          setPolygones(validatedPolygones);
        } else {
          setPolygones([]);
        }

        // Traitement des projets (regroupement avec les points déjà validés)
        if (projetsResponse && projetsResponse.ok) {
          const projetsData = await projetsResponse.json();
          const rawProjets: unknown[] = Array.isArray(projetsData) ? projetsData :
            Array.isArray(projetsData.projets) ? projetsData.projets : [];
          setProjets(attachPoints(rawProjets, validatedPoints));
        } else {
          setProjets([]);
        }
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
        setError(`Impossible de charger les données: ${errorMessage}`);
        setPoints([]);
        setPolygones([]);
        setProjets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, [slug]);

  const handleReload = (): void => {
    window.location.reload();
  };

  return useMemo(() => ({
    points,
    polygones,
    categories,
    projets,
    loading,
    error,
    mapKey: mapKey.current,
    handleReload,
  }), [points, polygones, categories, projets, loading, error]);
};
